/**
 * La campana del encabezado.
 *
 * POR QUÉ SE REESCRIBIÓ. Era un `ActionList` de Polaris — el componente de los menús contextuales,
 * pensado para "Editar / Duplicar / Eliminar". Una notificación no es una acción de menú: tiene
 * ícono, dos líneas de texto, hora y estado de leído. Metida en un ActionList quedaba con el título
 * en negrita, el cuerpo en letra chica de ayuda y una etiqueta "Nuevo" que empujaba todo — apretado
 * y sin jerarquía. Ahora usa el MISMO marco que el panel de chats, así los dos desplegables del
 * encabezado se abren igual, miden igual y scrollean igual.
 *
 * 🪤 LA HORA VIENE DEL MISMO HELPER QUE LOS CHATS. Que una lista diga "2 h" y la de al lado "hace 2
 * horas" es de esas incoherencias que hacen sentir la app cosida a pedazos.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Text } from '@/lib/polaris';
import {
  NotificationIcon, ChatIcon, OrderIcon, AlertTriangleIcon, CashDollarIcon,
} from '@/lib/polaris-icons';
import { useNotifications, type AppNotification } from '@/hooks/useNotifications';
import { waTimeAgo } from '@/components/whatsapp/wa-helpers';
import { PanelDelEncabezado, FilaDelPanel } from '@/components/layout/PanelDelEncabezado';

/**
 * Ícono y color por familia de notificación. El prefijo del `type` ("order.paid", "wa.mention")
 * alcanza: agrupa sin obligar a mantener una lista de todos los tipos, que crece cada vez que
 * alguien agrega uno.
 */
function pinta(tipo: string): { icono: typeof NotificationIcon; fondo: string } {
  const familia = tipo.split('.')[0];
  switch (familia) {
    case 'order': return { icono: OrderIcon, fondo: '#2C6ECB' };
    case 'wa': case 'chat': case 'message': return { icono: ChatIcon, fondo: '#25D366' };
    case 'payment': case 'cobranza': return { icono: CashDollarIcon, fondo: '#00848E' };
    case 'copilot': case 'alert': case 'integration': return { icono: AlertTriangleIcon, fondo: '#B98900' };
    default: return { icono: NotificationIcon, fondo: '#5C5F62' };
  }
}

function Insignia({ tipo }: { tipo: string }) {
  const { icono, fondo } = pinta(tipo);
  return (
    <span style={{
      width: 40, height: 40, borderRadius: '50%', background: fondo, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ width: 20, height: 20, display: 'block' }}>
        <Icon source={icono} tone="inherit" />
      </span>
    </span>
  );
}

export function NotificacionesMenu() {
  const [abierto, setAbierto] = useState(false);
  const [pestania, setPestania] = useState<'todas' | 'sin_leer'>('todas');
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications();

  const visibles = useMemo(
    () => (pestania === 'sin_leer' ? notifications.filter((n) => !n.read_at) : notifications),
    [notifications, pestania],
  );

  const abrir = (n: AppNotification) => {
    setAbierto(false);
    void markRead(n.id);
    if (n.url) navigate(n.url);
  };

  const activador = useMemo(() => (
    <button
      type="button"
      onClick={() => setAbierto((v) => !v)}
      aria-label={unreadCount > 0 ? `Notificaciones (${unreadCount} sin leer)` : 'Notificaciones'}
      // El fondo gris del encabezado sale de `aria-expanded` — ver la nota en WaChatsMenu: antes lo
      // tenía siempre el ícono de wifi y nunca este.
      aria-expanded={abierto}
      aria-haspopup="menu"
      className="sf-topbar-icon-button"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', width: 36, height: 36,
        background: 'transparent', border: 'none', borderRadius: 8,
        cursor: 'pointer', color: 'inherit',
      }}
    >
      <Icon source={NotificationIcon} tone="base" />
      {unreadCount > 0 && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', top: 1, right: 1, minWidth: 15, height: 15,
            padding: '0 3px', borderRadius: 9, background: 'var(--p-color-bg-fill-critical)',
            color: '#fff', fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  // 🪤 `abierto` VA EN LAS DEPENDENCIAS O EL FONDO NUNCA APARECE. El activador está memoizado; sin
  // esta dependencia el `aria-expanded` queda congelado en el valor del primer render y la regla de
  // CSS que pinta el ícono abierto no se activa jamás. El lint lo marcó como aviso, pero acá el
  // efecto es funcional: la corrección visual entera no se vería.
  ), [unreadCount, abierto]);

  return (
    <PanelDelEncabezado
      abierto={abierto}
      onCerrar={() => setAbierto(false)}
      activador={activador}
      titulo="Notificaciones"
      cargando={loading}
      pestanias={[
        { id: 'todas', texto: 'Todas' },
        { id: 'sin_leer', texto: 'No leídas', contador: unreadCount },
      ]}
      pestaniaActiva={pestania}
      onPestania={(id) => setPestania(id as typeof pestania)}
      // La acción aparece solo si hay algo que marcar: un botón que no hace nada enseña a ignorarlo.
      accion={unreadCount > 0 ? { texto: 'Marcar todas como leídas', onClick: () => void markAllRead() } : undefined}
      vacio={(
        <Text as="p" tone="subdued">
          {pestania === 'sin_leer'
            ? 'No hay notificaciones sin leer. Todo al día.'
            : 'No tenés notificaciones. Acá van a aparecer los avisos de pedidos, pagos e integraciones.'}
        </Text>
      )}
    >
      {visibles.map((n) => (
        <FilaDelPanel
          key={n.id}
          onClick={() => abrir(n)}
          avatar={<Insignia tipo={n.type} />}
          titulo={n.title}
          detalle={n.body}
          hora={waTimeAgo(n.created_at)}
          sinLeer={!n.read_at}
        />
      ))}
    </PanelDelEncabezado>
  );
}
